import { useState } from 'react';
import SafeIcon from './SafeIcon';
import './setup.css';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { postStatusHoldsDailySlot, useDemo, useWorkspaceData } from '../store/DemoStore';
import { channelFormats } from '../store/campaigns';
import { DEMO_TODAY, onAccount } from '../store/seed';
import type { Account } from '../store/seed';
import type { Channel } from '../types';

type AccountKind = {
  id: string;
  network: Channel;
  type: string;
  label: string;
  hint: string;
  personal?: boolean;
};
const publishableAccountKinds: AccountKind[] = [
  {
    id: 'ig',
    network: 'Instagram',
    type: 'Business account',
    label: 'Instagram business or creator account',
    hint: 'Personal Instagram accounts cannot publish through AMY.',
  },
  {
    id: 'fb',
    network: 'Facebook',
    type: 'Facebook Page',
    label: 'Facebook Page',
    hint: 'A Page you manage. Personal Facebook profiles are not supported.',
  },
  { id: 'tt', network: 'TikTok', type: 'TikTok', label: 'TikTok account', hint: 'Video only.' },
  {
    id: 'li',
    network: 'LinkedIn',
    type: 'LinkedIn company page',
    label: 'LinkedIn company page',
    hint: 'A page where you are an admin.',
  },
  {
    id: 'lip',
    network: 'LinkedIn',
    type: 'LinkedIn personal',
    label: 'Someone’s personal LinkedIn profile',
    hint: 'Only that person can connect it. AMY sends them a connect-only invitation.',
    personal: true,
  },
];

type ConnectStep = 'network' | 'consent' | 'pick' | 'confirm' | 'done' | 'personal';

const numberedConnectSteps = ['network', 'consent', 'pick', 'confirm'];

export default function ConnectSheet({
  account,
  onClose,
  asPerson,
}: {
  account?: Account;
  onClose: () => void;
  asPerson?: string;
}) {
  const { translate, language, readOnly } = useApp();
  const { route, actor, notify } = useUi();
  const { commands } = useDemo();
  const { workspace, accounts, posts, members } = useWorkspaceData(route.workspaceId);
  const isInvitedPersonOpeningConnectLink = !!asPerson;
  const reconnect = !!account;
  const neverConnected = account?.health === 'Not connected';
  const startKind = account
    ? publishableAccountKinds.find(
        (candidate) => candidate.network === account.network && candidate.type === account.type
      )
    : undefined;
  const [kind, setKind] = useState<AccountKind | undefined>(startKind);
  const chooseInitialStep = (): ConnectStep => {
    if (isInvitedPersonOpeningConnectLink) return 'consent';
    if (!account) return 'network';
    const isSomeoneElsesPersonalProfile = startKind?.personal && account.owner !== actor.name;
    return isSomeoneElsesPersonalProfile ? 'personal' : 'consent';
  };
  const [step, setStep] = useState<ConnectStep>(chooseInitialStep);
  const [picked, setPicked] = useState<string | undefined>(account?.name);
  const [person, setPerson] = useState(asPerson ?? account?.owner ?? '');
  const connectingPersonName = asPerson ?? actor.name;
  const workspaceHandle = workspace.name.toLowerCase().replace(/[^a-z0-9]+/g, '');
  const formatDay = (day: number) =>
    new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short' }).format(
      new Date(2026, 10, day)
    );

  const demoAccountsManagedBySignedInPerson = (
    accountKind: AccountKind
  ): { name: string; ok: boolean; reason?: string }[] =>
    accountKind.network === 'Instagram'
      ? [
          { name: `@${workspaceHandle}`, ok: true },
          {
            name: `@${actor.name.split(' ')[0].toLowerCase()}.photos`,
            ok: false,
            reason:
              'Personal account. Switch it to a business or creator account in Instagram first.',
          },
        ]
      : accountKind.network === 'Facebook'
        ? [
            { name: workspace.name, ok: true },
            { name: `${workspace.name} Events`, ok: true },
          ]
        : accountKind.network === 'TikTok'
          ? [{ name: `@${workspaceHandle}`, ok: true }]
          : accountKind.personal
            ? [{ name: connectingPersonName, ok: true }]
            : [{ name: workspace.name, ok: true }];
  const findOtherConnectedAccountNamed = (name: string) =>
    kind &&
    accounts.find(
      (candidate) =>
        candidate.network === kind.network &&
        candidate.type === kind.type &&
        candidate.name === name &&
        candidate.health === 'Connected' &&
        candidate.id !== account?.id
    );
  const postUsesThisChannel = (post: (typeof posts)[number]) =>
    account ? onAccount(post, account) : post.channel === kind!.network && !kind!.personal;
  const affected = kind
    ? posts.filter(
        (post) =>
          postUsesThisChannel(post) &&
          postStatusHoldsDailySlot(post.status) &&
          post.status !== 'Published' &&
          post.day >= DEMO_TODAY
      )
    : [];
  const replacing = reconnect && picked && picked !== account?.name;
  const approved = affected.filter(
    (post) => post.status === 'Scheduled' || post.status === 'Approved · Stopped'
  );

  function finish() {
    if (!kind || !picked || readOnly) return;
    const connectionResult = commands.connectAccount(
      workspace.id,
      { network: kind.network, name: picked, type: kind.type },
      asPerson ? { ...actor, name: asPerson } : actor,
      account?.id
    );
    setStep('done');
    notify(
      connectionResult.reapproval
        ? translate('{account} is connected. {n} posts moved to it and need approval again.', {
            account: picked,
            n: connectionResult.reapproval,
          })
        : translate('{account} is connected to {ws}.', { account: picked, ws: workspace.name }),
      connectionResult.reapproval ? 'warn' : undefined
    );
  }
  function invitePerson() {
    if (!person || readOnly) return;
    const existingPersonalAccount = accounts.find(
      (candidate) => candidate.type === 'LinkedIn personal' && candidate.owner === person
    );
    const id = existingPersonalAccount?.id ?? `${workspace.id}-lip-${Date.now()}`;
    if (!existingPersonalAccount)
      commands.addAccount({
        id,
        workspaceId: workspace.id,
        network: 'LinkedIn',
        name: person,
        type: 'LinkedIn personal',
        health: 'Not connected',
        owner: person,
      });
    commands.remind(`connect-${id}`);
    notify(translate('Connect-only invitation sent to {name} (simulated)', { name: person }));
    onClose();
  }

  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="drawer connect-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="connect-title"
      >
        <div className="drawer-header">
          <div>
            <h2 id="connect-title">
              {reconnect
                ? translate(neverConnected ? 'Connect {account}' : 'Reconnect {account}', {
                    account: account!.name,
                  })
                : translate('Connect an account')}
            </h2>
            <div className="muted">{translate('For {ws}', { ws: workspace.name })}</div>
          </div>
          <button className="icon-button" onClick={onClose} aria-label={translate('Close')}>
            <SafeIcon name="FiX" />
          </button>
        </div>
        {step !== 'done' && step !== 'personal' && (
          <ol className="connect-steps" aria-label={translate('Steps')}>
            {numberedConnectSteps.map((stepName, stepIndex) => (
              <li key={stepName} className={stepName === step ? 'current' : ''}>
                {stepIndex + 1}.{' '}
                {translate(
                  {
                    network: 'Network',
                    consent: 'Allow access',
                    pick: 'Choose account',
                    confirm: 'Confirm',
                  }[stepName]!
                )}
              </li>
            ))}
          </ol>
        )}

        {step === 'network' && (
          <div className="connect-kinds">
            {publishableAccountKinds.map((accountKind) => (
              <button
                key={accountKind.id}
                className={`connect-kind ${kind?.id === accountKind.id ? 'selected' : ''}`}
                onClick={() => {
                  setKind(accountKind);
                  setPicked(undefined);
                  setStep(accountKind.personal ? 'personal' : 'consent');
                }}
              >
                <strong>{translate(accountKind.label)}</strong>
                <span className="muted small-print">{translate(accountKind.hint)}</span>
                {!accountKind.personal && (
                  <span className="small-print">
                    {translate('Can publish')}:{' '}
                    {channelFormats[accountKind.network as keyof typeof channelFormats]
                      .map((format) => translate(format))
                      .join(', ')}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {step === 'personal' && (
          <div>
            <p>
              {translate(
                'A personal LinkedIn profile belongs to one person, so only they can connect it. Choose who; they get a link that does nothing except connect their profile to {ws}.',
                { ws: workspace.name }
              )}
            </p>
            <label className="wizard-field">
              {translate('Person')}
              <select
                className="select-field"
                value={person}
                onChange={(event) => setPerson(event.target.value)}
              >
                <option value="">{translate('Choose a person')}</option>
                {members
                  .filter((member) => member.status === 'Active')
                  .map((member) => (
                    <option key={member.id} value={member.name}>
                      {member.name}
                    </option>
                  ))}
              </select>
            </label>
            {person === actor.name && (
              <p className="muted small-print">
                {translate('This is you, so you can connect your own profile now.')}
              </p>
            )}
            <div className="picker-footer">
              <button
                className="button"
                onClick={() => (reconnect ? onClose() : setStep('network'))}
              >
                {translate(reconnect ? 'Cancel' : 'Back')}
              </button>
              {person === actor.name ? (
                <button
                  className="button primary"
                  disabled={readOnly}
                  onClick={() => {
                    setKind(publishableAccountKinds.find((accountKind) => accountKind.personal));
                    setPicked(undefined);
                    setStep('consent');
                  }}
                >
                  <SafeIcon name="FiLink" />
                  {translate('Connect my own profile')}
                </button>
              ) : (
                <button
                  className="button primary"
                  disabled={!person || readOnly}
                  onClick={invitePerson}
                >
                  <SafeIcon name="FiSend" />
                  {translate('Send connect invitation')}
                </button>
              )}
            </div>
          </div>
        )}

        {step === 'consent' && kind && (
          <div>
            <div className="consent-mock">
              <span className="tag">
                {translate('Simulated {network} screen', { network: kind.network })}
              </span>
              <h3>
                {translate('Allow AMY to use your {network} account?', { network: kind.network })}
              </h3>
              <ul>
                <li>{translate('Publish posts you approve in AMY')}</li>
                <li>{translate('Read basic results such as reach and likes')}</li>
              </ul>
              <p className="muted small-print">
                {translate('AMY cannot read private messages or change your password.')}
              </p>
            </div>
            <div className="picker-footer">
              <button
                className="button"
                onClick={() => (reconnect ? onClose() : setStep('network'))}
              >
                {translate(reconnect ? 'Cancel' : 'Back')}
              </button>
              <button
                className="button primary"
                onClick={() => {
                  if (kind.personal) setPicked(connectingPersonName);
                  setStep('pick');
                }}
              >
                {translate('Allow')}
              </button>
            </div>
          </div>
        )}

        {step === 'pick' && kind && (
          <div>
            <p>{translate('Accounts you manage on {network}:', { network: kind.network })}</p>
            <div
              role="radiogroup"
              aria-label={translate('Choose account')}
              className="connect-kinds"
            >
              {demoAccountsManagedBySignedInPerson(kind).map((choice) => {
                const duplicateAccount = findOtherConnectedAccountNamed(choice.name);
                const choiceDescription = (() => {
                  if (!choice.ok) return translate(choice.reason!);
                  if (duplicateAccount)
                    return translate('Already connected to {ws}', { ws: workspace.name });
                  if (choice.name === account?.name)
                    return translate('The account connected before');
                  return translate(kind.type);
                })();
                return (
                  <label
                    key={choice.name}
                    className={`connect-kind ${picked === choice.name ? 'selected' : ''} ${!choice.ok || duplicateAccount ? 'disabled' : ''}`}
                  >
                    <input
                      type="radio"
                      name="pick-account"
                      disabled={!choice.ok || !!duplicateAccount}
                      checked={picked === choice.name}
                      onChange={() => setPicked(choice.name)}
                    />{' '}
                    <strong>{choice.name}</strong>
                    <span className="muted small-print">{choiceDescription}</span>
                  </label>
                );
              })}
            </div>
            {replacing && (
              <AccountReplacementWarning
                oldName={account!.name}
                newName={picked!}
                posts={affected}
                approved={approved.length}
                formatDay={formatDay}
              />
            )}
            <div className="picker-footer">
              <button className="button" onClick={() => setStep('consent')}>
                {translate('Back')}
              </button>
              <button
                className="button primary"
                disabled={!picked}
                onClick={() => setStep('confirm')}
              >
                {translate('Continue')}
              </button>
            </div>
          </div>
        )}

        {step === 'confirm' && kind && picked && (
          <div>
            <div className="panel inset">
              <strong>{picked}</strong> · {translate(kind.type)}
              <div className="muted">
                {translate('Connect to workspace {ws}', { ws: workspace.name })}
              </div>
              <p className="small-print">
                {translate('Can publish')}:{' '}
                {channelFormats[kind.network as keyof typeof channelFormats]
                  .map((format) => translate(format))
                  .join(', ')}
              </p>
              {replacing && (
                <AccountReplacementWarning
                  oldName={account!.name}
                  newName={picked}
                  posts={affected}
                  approved={approved.length}
                  formatDay={formatDay}
                />
              )}
              {!replacing && affected.length > 0 && (
                <p className="small-print">
                  {translate('{n} upcoming {network} posts use this channel, the next on {date}.', {
                    n: affected.length,
                    network: kind.network,
                    date: formatDay(Math.min(...affected.map((post) => post.day))),
                  })}{' '}
                  {translate(
                    reconnect
                      ? 'They keep their approval and publish on time.'
                      : 'They publish to this account once approved.'
                  )}
                </p>
              )}
            </div>
            <div className="picker-footer">
              <button className="button" onClick={() => setStep('pick')}>
                {translate('Back')}
              </button>
              <button className="button primary" onClick={finish} disabled={readOnly}>
                <SafeIcon name="FiLink" />
                {replacing
                  ? translate('Replace account')
                  : reconnect && !neverConnected
                    ? translate('Reconnect')
                    : translate('Connect')}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && kind && (
          <div className="guest-done">
            <SafeIcon name="FiCheckCircle" size={32} />
            <h3>{translate('{account} is connected', { account: picked! })}</h3>
            <p className="muted">
              {translate('Shown the same way in the sidebar, Settings, setup and the calendar.')}
            </p>
            <button className="button primary" onClick={onClose}>
              {translate('Done')}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

function AccountReplacementWarning({
  oldName,
  newName,
  posts,
  approved,
  formatDay,
}: {
  oldName: string;
  newName: string;
  posts: { id: string; title: string; day: number; time: string; status: string }[];
  approved: number;
  formatDay: (day: number) => string;
}) {
  const { translate } = useApp();
  return (
    <div className="notice-banner warn replace-note">
      <SafeIcon name="FiRepeat" />
      <div>
        <strong>{translate('Replace {old} with {new}', { old: oldName, new: newName })}</strong>
        <div>
          {translate('{n} upcoming posts move to {new}.', { n: posts.length, new: newName })}{' '}
          {approved > 0 &&
            translate(
              '{n} of them were approved for {old}; they go back to review, so nothing is published to the new account without approval.',
              { n: approved, old: oldName }
            )}
        </div>
        {posts.length > 0 && (
          <ul>
            {posts.map((post) => (
              <li key={post.id}>
                {post.title} · {formatDay(post.day)} {post.time} · {translate(post.status)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
